/**
 * One impact stage, mounted over a board that draws itself in 2D.
 *
 * Both boards — the player's PlayerTacticalMap and the GM's PlayerCanvas — need the
 * same three things and nothing more: a WebGL canvas stacked over the painted floor,
 * the board's own geometry pushed into it as four corners, and a frame loop that runs
 * only while something is actually moving. This is that, once, so the second board is
 * a wiring job rather than a copy.
 *
 * Two properties it exists to guarantee:
 *
 *  - THE PROJECTION IS READ, NEVER STORED. The board owns pan, zoom and the corner
 *    morph, and changes all three without telling anyone. So the layer asks for a
 *    fresh `GridProjection` on every frame it draws instead of subscribing to events
 *    that would have to be discovered and kept in step. Nothing to miss, nothing to
 *    invalidate.
 *
 *  - THE LOOP IS GATED. A turn-based board is idle almost all of the time; a rAF
 *    running behind it costs a composite per frame forever. The loop starts when a
 *    cast or an effect arrives and stops itself the frame after the last one leaves,
 *    having drawn that empty frame so the layer clears.
 *
 * A lost context is survivable and is survived: the stage is rebuilt on restore.
 * Whatever was mid-flight is gone with it — an animation that outlives a GPU reset
 * is not worth the bookkeeping, and the board underneath never went anywhere.
 */

import { ImpactEffectStage } from '@/services/impact/ImpactEffectStage';
import { GridProjection, readGridConfig, scaleGridToImage } from '@/services/impact/projection';

const MAX_FRAME_MS = 50;

export class ImpactBoardLayer {
  /**
   * @param canvas   the effects canvas, stacked over the board's own
   * @param options  projection  () => GridProjection | null, read every frame
   *                 quality     stage quality tier
   *                 onFrame     called after each rendered frame
   *                 onLost      called when the GPU context goes away
   *                 onRestored  called once the stage is rebuilt
   */
  constructor(canvas, { projection = () => null, quality = 'high', record = false, onFrame = null, onLost = null, onRestored = null } = {}) {
    this.canvas = canvas;
    this.readProjection = projection;
    this.quality = quality;
    this.record = record;
    this.onFrame = onFrame;
    this.onLost = onLost;
    this.onRestored = onRestored;

    this.stage = null;
    this.frameId = null;
    this.last = 0;
    this.viewport = { width: 0, height: 0 };
    this.disposed = false;

    this.handleContextLost = (event) => {
      // Without this the browser never fires `webglcontextrestored` at all.
      event.preventDefault();
      this.stopLoop();
      this.stage = null;
      this.onLost?.();
    };
    this.handleContextRestored = () => {
      if (this.disposed) return;
      if (this.createStage()) {
        this.resize(this.viewport.width, this.viewport.height);
        this.onRestored?.();
      }
    };

    canvas.addEventListener('webglcontextlost', this.handleContextLost);
    canvas.addEventListener('webglcontextrestored', this.handleContextRestored);

    this.createStage();
  }

  createStage() {
    try {
      this.stage = new ImpactEffectStage(this.canvas, { quality: this.quality, record: this.record });
      /* An impact is over faster than a screenshot round-trip; this is the only
         handle a dev session has on the layer's clock. */
      if (import.meta.env.DEV && typeof window !== 'undefined') window.__impactLayer = this;
      return true;
    } catch (error) {
      // No WebGL is a board without effects, never a board that fails to draw.
      console.warn('[ImpactBoardLayer] no WebGL; the effects layer stays dark:', error);
      this.stage = null;
      return false;
    }
  }

  get ready() {
    return !!this.stage;
  }

  resize(width, height) {
    if (!(width > 0) || !(height > 0)) return;
    this.viewport = { width, height };
    this.stage?.resize(width, height);
    this.wake();
  }

  /** Whether anything on the stage still needs frames. */
  hasWork() {
    if (!this.stage) return false;
    return this.stage.running.length > 0 || this.stage.effects.size > 0;
  }

  wake() {
    if (this.disposed || !this.stage || this.frameId !== null) return;
    if (!this.hasWork()) return;
    this.last = performance.now();
    this.frameId = requestAnimationFrame(this.frame);
  }

  frame = (now) => {
    this.frameId = null;
    if (!this.stage) return;

    const dt = Math.min(MAX_FRAME_MS, now - this.last);
    this.last = now;

    const projection = this.readProjection();
    if (projection) this.stage.setProjection(projection);
    this.stage.update(dt, now);
    this.stage.render();
    this.onFrame?.(dt, now);

    // Scheduled after the draw: the frame that retires the last effect is also the
    // frame that renders the layer empty, so nothing is left burned onto the board.
    if (this.hasWork()) this.frameId = requestAnimationFrame(this.frame);
  };

  stopLoop() {
    if (this.frameId !== null) cancelAnimationFrame(this.frameId);
    this.frameId = null;
  }

  playCast(cast, hooks = {}) {
    if (!this.stage) return Promise.resolve(null);
    const played = this.stage.playCast(cast, hooks);
    this.wake();
    return played;
  }

  setEffect(instance) {
    if (!this.stage) return null;
    const effect = this.stage.setEffect(instance);
    this.wake();
    return effect;
  }

  setPhase(phase, id = null) {
    this.stage?.setPhase(phase, id);
    this.wake();
  }

  removeEffect(id) {
    this.stage?.removeEffect(id);
    this.wake();
  }

  /** Force-finish every cast in flight, without touching held effects like pending markers. */
  stopCasts() {
    this.stage?.stopCasts();
    this.wake();
  }

  clear() {
    this.stage?.stopCasts();
    this.stage?.clear();
    this.stage?.render();
    this.stopLoop();
  }

  dispose() {
    this.disposed = true;
    this.stopLoop();
    this.canvas.removeEventListener('webglcontextlost', this.handleContextLost);
    this.canvas.removeEventListener('webglcontextrestored', this.handleContextRestored);
    this.stage?.dispose();
    this.stage = null;
  }
}

/**
 * One queue for every cast a board draws.
 *
 * `playCast` resolves when its cast has finished leaving, and two blasts at once on a
 * small board read as noise rather than as two things having happened — so the whole
 * round drains one at a time, and a cycle boundary landing while the player is
 * mid-swing lines up behind them instead of over them.
 *
 * `after` is the queue already in flight; pass the promise this call last returned.
 * The returned promise NEVER rejects: a cast that throws must not poison the barrier
 * the next round waits on, and a swallowed failure that nobody logs is a reel that
 * silently stops. Both boards used to own a byte-identical copy of this, and both
 * discarded the error without a word.
 */
export function playSequential(casts, play, after = null) {
  const run = (async () => {
    if (after) await after;
    for (const cast of casts || []) {
      await play(cast);
    }
  })();
  return run.catch(error => {
    console.warn('[impact] a cast failed mid-reel; the queue moves on:', error);
    return null;
  });
}

/**
 * The board's numbers, as the shader wants them.
 *
 * `origin` is the grid's top-left in UNZOOMED canvas pixels — the same point the 2D
 * layers start their morph from — because zoom is applied about the viewport centre
 * afterwards, exactly as the 2D context's own transform does it. Passing an
 * already-zoomed origin scales the board twice.
 *
 * `gridSettings` is the raw grid as the server or the editor stores it; the rescale
 * onto the loaded image happens here, so no caller is tempted to do it a second time.
 */
export function boardProjection({ gridSettings, image = null, origin, zoom = 1, viewport }) {
  const config = scaleGridToImage(readGridConfig(gridSettings), image);
  if (!config) return null;
  return new GridProjection(config, { origin, zoom, viewport });
}

/**
 * Drop what the player cannot see.
 *
 * The fog is painted into the 2D canvas UNDER this layer, so DOM order cannot hide
 * anything for us: a cell the server withheld has to never reach the stage. A board
 * with no vision block returns everything, which is what an unfogged map means.
 */
export function maskCellsToVision(cells, visibleCellKeys) {
  if (!visibleCellKeys) return cells || [];
  return (cells || []).filter(cell => visibleCellKeys.has(`${cell.col},${cell.row}`));
}

export default ImpactBoardLayer;
